import { PageController } from '@client/components/_core/layout/PageController';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { NotFound } from '@client/components/NotFound';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useIsUIDebugInfoEnabled } from '@client/hooks/system/useIsUIDebugInfoEnabled';
import type { TableColumn } from '@client/hooks/table/useAntdTable';
import { createColumnHelper, usePrismaTable } from '@client/hooks/table-v2';
import { PrismaTable } from '@client/hooks/table-v2/components/PrismaTable';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/mspr/msp-exceptions')({
    staticData: {
        permissions: ['msp:read'],
        ui: {
            title: 'MSP exceptions',
            subtitle: 'Track and manage material service provider exceptions',
        },
    },
    component: MspExceptions,
});

type ExcludedCategory = RouterOutputs['mspr']['category']['listExcludedCategories']['rows'][number];
type QueryParams = RouterInputs['mspr']['category']['listExcludedCategories'];

function MspExceptions() {
    const { trpc } = useTrpc();
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();
    const isUIDebugInfoEnabled = useIsUIDebugInfoEnabled();
    const user = useUser({ throwOnUndefined: false });
    const canReadMspr = hasPermission(user?.roles ?? [], 'msp:read');
    const [queryParams, setQueryParams] = useState<QueryParams>({ limit: DEFAULT_PAGE_SIZE, offset: 0 });

    const {
        data: excludedCategoriesResult,
        isPending: isPendingExcludedCategories,
        error: excludedCategoriesError,
        refetch,
    } = trpc.mspr.category.listExcludedCategories.useQuery(queryParams, { enabled: canReadMspr });
    const excludedCategories = excludedCategoriesResult?.rows;

    if (!canReadMspr) {
        return <NotFound />;
    }

    const columnHelper = createColumnHelper<ExcludedCategory, any>();

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: excludedCategories || [],
        pagination: {
            position: 'both',
        },
        queryParams,
        onParamsChange: setQueryParams,
        // Filters, sort and page survive a refresh (CUR-1341).
        urlKey: 'exceptions',
        queryError: excludedCategoriesError,
        emptyState: { noRecordsTitle: 'No excluded categories' },
        total: excludedCategoriesResult?.total || 0,
        loading: isPendingExcludedCategories,
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        bordered: true,
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Category',
                cell: ({ row }) => <span className="font-medium text-foreground">{row.name}</span>,
                hideable: false,
            }),
            columnHelper.accessor('riskSignedOffDate', {
                id: 'riskSignedOffDate',
                header: 'Risk signed off date',
                cell: ({ row }) => formatDate(row.riskSignedOffDate),
            }),
            columnHelper.accessor('rationaleForExclusion', {
                id: 'rationaleForExclusion',
                header: 'Rationale for exclusion',
                cell: ({ row }) => <ComponentPreview setHtml>{row.rationaleForExclusion}</ComponentPreview>,
            }),
            ...(isUIDebugInfoEnabled
                ? ([
                      columnHelper.accessor('id', {
                          id: 'id',
                          header: 'ID',
                          cell: ({ row }) => BigInt(row.id).toString(),
                      }),
                      columnHelper.accessor('orgId', {
                          id: 'orgId',
                          header: 'Org ID',
                          cell: ({ row }) => BigInt(row.orgId).toString(),
                      }),
                  ] satisfies TableColumn<ExcludedCategory>[])
                : []),
        ],
    });

    return (
        <PageController route={Route}>
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer hover:bg-muted',
                    onClick() {
                        navigate({
                            to: '/console/$orgId/mspr/msp-exceptions/edit/$id',
                            params: { orgId, id: id },
                        });
                    },
                })}
            />
            <Outlet />
        </PageController>
    );
}
